import React from 'react';
import { useParams } from 'react-router-dom';
import { Person, WithContext } from 'schema-dts';
import Layout from '~/src/components/Layout';
import LoadingBoundary from '~/src/components/LoadingBoundary';
import Profile from '~/src/components/Player/Profile';
import Tabs from '~/src/components/Player/Tabs';
import Header from '~/src/components/Team/Header';
import { positions } from '~/src/lib';
import { Urls } from '~/src/lib/enums/urls';
import { OtePlayer } from '~/src/models/OtePlayer';
import NotFound from '~/src/overtime-lib/src/www/components/NotFound';
import ValidateUUID from '~/src/overtime-lib/src/www/components/ValidateUUID';
import { useSetDocumentState } from '~/src/overtime-lib/src/www/contexts/Document';
import useFetch from '~/src/overtime-lib/src/www/hooks/useFetch';
import { colorFromInt } from '~/src/overtime-lib/src/www/lib';
import './Player.scss';

const _Player = () => {
	const { id } = useParams();

	const fetchResult = useFetch<OtePlayer>({
		url: `${Urls.ApiRestOtePlayersUrl}/${id}`,
		key: 'ote_player',
	});
	const player = fetchResult.result?.read();
	if (!player) {
		return <NotFound />;
	}
	const team = player.ote_team;

	const descriptionTemplate = `${player.full_name} is a${
		player.height ? ` ${Math.floor(player.height / 12)}'${player.height % 12}"` : ''
	}${player.position ? ` ${positions[player.position].toLowerCase()}` : ''}${
		player.hometown ? ` from ${player.hometown}` : ''
	}.${
		player.ote_team
			? player.is_current
				? ` He currently plays for the ${player.ote_team.name}.`
				: ` He played for the ${player.ote_team.name}${
						player.professional_team ? ` and currently plays for the ${player.professional_team.name}` : ''
					}.`
			: player.professional_team
				? ` He currently plays for the ${player.professional_team.name}.`
				: ''
	}`;

	const description = player.share_description || descriptionTemplate;

	useSetDocumentState({
		title: player.full_name,
		image: [
			player.hero_image_path ? `https://images.overtime.tv/${player.hero_image_path}` : undefined,
			player.image_path ? `https://images.overtime.tv/${player.image_path}` : undefined,
		].filter(Boolean),
		description,
		color: colorFromInt(player.ote_team?.primary_color),
	});

	const data: WithContext<Person> = {
		'@context': 'https://schema.org',
		'@type': 'Person',
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': `https://overtimeelite.com/players/${player.id}`,
		},
		affiliation: player.ote_team
			? {
					'@type': 'SportsTeam',
					name: player.ote_team?.name,
					url: `https://overtimeelite.com/teams/${player.ote_team?.slug}`,
				}
			: undefined,
		description,
		birthDate: player.birthdate,
		image: player.image_path ? `https://images.overtime.tv/${player.image_path}` : undefined,
		name: player.full_name,
		givenName: player.first_name,
		familyName: player.family_name,
		height: player.height
			? [
					{
						'@type': 'QuantitativeValue',
						value: player.height,
						unitCode: 'INH',
					},
				]
			: undefined,
		weight: player.weight
			? [
					{
						'@type': 'QuantitativeValue',
						value: player.weight,
						unitCode: 'LBR',
					},
				]
			: undefined,
	};

	return (
		<div className="Player">
			<Header team={team} />
			<Profile player={player} team={team} />

			<Tabs player={player} team={team} />

			<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}></script>
		</div>
	);
};

const Player = () => {
	const { id } = useParams();

	return (
		<Layout>
			<LoadingBoundary>
				<ValidateUUID id={id} redirectPrefix={'/players'}>
					<_Player />
				</ValidateUUID>
			</LoadingBoundary>
		</Layout>
	);
};
export default Player;
